import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    size: number
    rotation: number
    spin: number
    life: number
    age: number
    color: string
}

const COLORS = ['#e2b33c', '#f3d27a', '#fbeab4', '#ffffff']
const SPACING = 16
const MAX_PARTICLES = 60

// A four-pointed ✦ centred on (0, 0), drawn like the site's sparkle icon.
const drawStar = (context: CanvasRenderingContext2D, size: number): void => {
    const r = size / 2
    const inner = r * 0.16

    context.beginPath()
    context.moveTo(0, -r)
    context.quadraticCurveTo(inner, -inner, r, 0)
    context.quadraticCurveTo(inner, inner, 0, r)
    context.quadraticCurveTo(-inner, inner, -r, 0)
    context.quadraticCurveTo(-inner, -inner, 0, -r)
    context.closePath()
    context.fill()
}

// The site's own cursor (a small ✦ with a fine ring, see globals.css) and a light trail of tiny
// stars that drift and fade behind it. Only for a mouse or trackpad: touch screens and visitors
// who prefer reduced motion keep their usual pointer and get no trail. The canvas never takes
// clicks, and nothing is drawn while the pointer is still.
const CursorSparkle: FC = (): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)')

        if (!fine.matches) return

        const root = document.documentElement

        root.classList.add('has-custom-cursor')

        const canvas = canvasRef.current
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const context = canvas?.getContext('2d') ?? null

        if (!canvas || !context || reduce) {
            return () => root.classList.remove('has-custom-cursor')
        }

        const particles: Array<Particle> = []
        let frame = 0
        let last: { x: number, y: number } | null = null
        let previousTime = 0

        const resize = (): void => {
            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            canvas.width = Math.round(window.innerWidth * ratio)
            canvas.height = Math.round(window.innerHeight * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
        }

        const spawn = (x: number, y: number): void => {
            if (particles.length >= MAX_PARTICLES) particles.shift()

            particles.push({
                x: x + (Math.random() - 0.5) * 10,
                y: y + (Math.random() - 0.5) * 10,
                vx: (Math.random() - 0.5) * 0.02,
                vy: 0.01 + Math.random() * 0.025,
                size: 4 + Math.random() * 6,
                rotation: Math.random() * Math.PI,
                spin: (Math.random() - 0.5) * 0.004,
                life: 650 + Math.random() * 450,
                age: 0,
                color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? COLORS[0]
            })
        }

        const tick = (time: number): void => {
            const delta = previousTime ? Math.min(time - previousTime, 48) : 16

            previousTime = time
            context.clearRect(0, 0, window.innerWidth, window.innerHeight)

            for (let index = particles.length - 1; index >= 0; index--) {
                const particle = particles[index]

                particle.age += delta

                if (particle.age >= particle.life) {
                    particles.splice(index, 1)
                    continue
                }

                const progress = particle.age / particle.life

                particle.x += particle.vx * delta
                particle.y += particle.vy * delta
                particle.rotation += particle.spin * delta

                context.save()
                context.globalAlpha = (1 - progress) * 0.9
                context.fillStyle = particle.color
                context.shadowColor = 'rgba(255, 226, 150, 0.8)'
                context.shadowBlur = 6
                context.translate(particle.x, particle.y)
                context.rotate(particle.rotation)
                drawStar(context, particle.size * (1 - progress * 0.6))
                context.restore()
            }

            frame = particles.length > 0 ? window.requestAnimationFrame(tick) : 0

            if (!frame) previousTime = 0
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return

            const point = { x: event.clientX, y: event.clientY }

            if (last) {
                const distance = Math.hypot(point.x - last.x, point.y - last.y)

                if (distance < SPACING) return
            }

            last = point
            spawn(point.x, point.y)

            if (!frame) frame = window.requestAnimationFrame(tick)
        }

        const onLeave = (): void => {
            last = null
        }

        resize()
        window.addEventListener('resize', resize)
        window.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('pointerleave', onLeave)

        return () => {
            root.classList.remove('has-custom-cursor')
            window.removeEventListener('resize', resize)
            window.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerleave', onLeave)

            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            aria-hidden='true'
            className='pointer-events-none fixed inset-0 z-[120] h-full w-full'
        />
    )
}

export default CursorSparkle
